import React, { memo, useCallback, useEffect } from 'react';

import { Box } from '@mui/material';

import { usePrepareDisclosuresWizard, useRegisterStepGate } from '../../../hooks';
import { WizardStep } from '../../../PrepareDisclosuresWizard.constants';

import KeyNumbersRail from './components/KeyNumbersRail';
import PreviewBaselineNote from './components/PreviewBaselineNote';
import PreviewDocumentPane from './components/PreviewDocumentPane';
import ReviewAttestationCard from './components/ReviewAttestationCard';
import { usePreviewDocument, usePreviewKeyNumbers } from './hooks';
import { PreviewStatus } from './previewGeneration.constants';
import { isPreviewStale } from './previewGeneration.utils';
import { layoutStyles, railStyles } from './PreviewStep.styles';
import { PreviewStepTestIds } from './PreviewStep.testIds';

const PreviewStep = () => {
  const { applicationId, loanId, reviewedVersionHash, setReviewedVersionHash, setPreviewResult } =
    usePrepareDisclosuresWizard();

  const { keyNumbers, versionHash } = usePreviewKeyNumbers();
  const { status, progress, errorMessage, getDocumentCall, formName, formBundleId, formId, retry } =
    usePreviewDocument({ applicationId, loanId, versionHash });

  const isReady = status === PreviewStatus.READY;
  const isReviewed = isReady && !isPreviewStale(reviewedVersionHash, versionHash);

  useRegisterStepGate(WizardStep.PREVIEW, isReviewed);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    setPreviewResult({
      formId,
      formName,
      formBundleId,
      versionHash,
      generatedAt: new Date().toISOString(),
      documentId: null,
      formDeliveryRequestId: null,
    });
  }, [isReady, formId, formName, formBundleId, versionHash, setPreviewResult]);

  const handleReviewChange = useCallback(
    nextChecked => setReviewedVersionHash(nextChecked ? versionHash : null),
    [setReviewedVersionHash, versionHash],
  );

  return (
    <Box data-testid={PreviewStepTestIds.ROOT} sx={layoutStyles}>
      <PreviewDocumentPane
        errorMessage={errorMessage}
        getDocumentCall={getDocumentCall}
        progress={progress}
        status={status}
        onRetry={retry}
      />
      <Box sx={railStyles}>
        <KeyNumbersRail keyNumbers={keyNumbers} />
        <PreviewBaselineNote />
        <ReviewAttestationCard
          checked={isReviewed}
          disabled={!isReady}
          onChange={handleReviewChange}
        />
      </Box>
    </Box>
  );
};

export default memo(PreviewStep);
